import React, { useMemo } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { GridColDef } from "@mui/x-data-grid-pro";
import {
  VehicleType,
  VehicleTypeValue,
  VehicleCategory,
  VehicleCategoryValue,
} from "@app.automotus.io/components/common";
import { EditBuffer, NewVehicleRow, VehicleRowErrors, VehicleTableRow } from "../vehicles.types";
import { EditableSelectCell } from "../components/cells/EditableSelectCell";
import { EditableTextCell } from "../components/cells/EditableTextCell";
import { STATE_OPTIONS, VEHICLE_TYPE_OPTIONS, VEHICLE_CATEGORY_OPTIONS } from "../vehicleOptions";

interface UseVehicleColumnsOptions {
  editingRowId: string | null;
  editBuffer: EditBuffer | null;
  editErrors: VehicleRowErrors;
  isSaving: boolean;
  disabled?: boolean;
  updateNewRow: (id: string, patch: Partial<NewVehicleRow>) => void;
  handleEditBufferChange: (patch: Partial<EditBuffer>) => void;
  handleStartEdit: (row: VehicleTableRow) => void;
  handleSave: () => Promise<void>;
  handleCancelEdit: () => void;
  handleDeleteRow: (id: string) => void;
}

export function useVehicleColumns({
  editingRowId,
  editBuffer,
  editErrors,
  isSaving,
  disabled,
  updateNewRow,
  handleEditBufferChange,
  handleStartEdit,
  handleSave,
  handleCancelEdit,
  handleDeleteRow,
}: UseVehicleColumnsOptions): GridColDef<VehicleTableRow>[] {
  return useMemo(
    () => [
      {
        field: "stateCode",
        headerName: "State",
        width: 160,
        sortable: false,
        type: "singleSelect",
        valueOptions: STATE_OPTIONS,
        renderCell: (params) => {
          if (params.row.isNew) {
            return (
              <EditableSelectCell
                value={params.row.stateCode}
                options={STATE_OPTIONS}
                hasError={!!params.row.errors?.stateCode}
                onChange={(v) => updateNewRow(params.row.id, { stateCode: v })}
                placeholder="State…"
              />
            );
          }
          if (params.row.id === editingRowId && editBuffer) {
            return (
              <EditableSelectCell
                value={editBuffer.stateCode}
                options={STATE_OPTIONS}
                hasError={!!editErrors.stateCode}
                onChange={(v) => handleEditBufferChange({ stateCode: v })}
                placeholder="State…"
              />
            );
          }
          return params.value ?? "-";
        },
      },
      {
        field: "licensePlate",
        headerName: "Plate Number",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          if (params.row.isNew) {
            return (
              <EditableTextCell
                value={params.row.licensePlate}
                hasError={!!params.row.errors?.licensePlate}
                onChange={(v) => updateNewRow(params.row.id, { licensePlate: v })}
                placeholder="ABC1234"
                transform={(v) => v.replace(/\s/g, "").toUpperCase()}
                inputProps={{ autoCapitalize: "characters" }}
              />
            );
          }
          if (params.row.id === editingRowId && editBuffer) {
            return (
              <EditableTextCell
                value={editBuffer.licensePlate}
                hasError={!!editErrors.licensePlate}
                onChange={(v) => handleEditBufferChange({ licensePlate: v })}
                placeholder="ABC1234"
                transform={(v) => v.replace(/\s/g, "").toUpperCase()}
                inputProps={{ autoCapitalize: "characters" }}
              />
            );
          }
          return params.value ?? "-";
        },
      },
      {
        field: "make",
        headerName: "Make",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          if (params.row.isNew) {
            return (
              <EditableTextCell
                value={params.row.make ?? ""}
                hasError={false}
                onChange={(v) => updateNewRow(params.row.id, { make: v })}
                placeholder="Toyota"
              />
            );
          }
          if (params.row.id === editingRowId && editBuffer) {
            return (
              <EditableTextCell
                value={editBuffer.make}
                hasError={false}
                onChange={(v) => handleEditBufferChange({ make: v })}
                placeholder="Toyota"
              />
            );
          }
          return params.value ?? "-";
        },
      },
      {
        field: "model",
        headerName: "Model",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          if (params.row.isNew) {
            return (
              <EditableTextCell
                value={params.row.model ?? ""}
                hasError={false}
                onChange={(v) => updateNewRow(params.row.id, { model: v })}
                placeholder="Camry"
              />
            );
          }
          if (params.row.id === editingRowId && editBuffer) {
            return (
              <EditableTextCell
                value={editBuffer.model}
                hasError={false}
                onChange={(v) => handleEditBufferChange({ model: v })}
                placeholder="Camry"
              />
            );
          }
          return params.value ?? "-";
        },
      },
      {
        field: "type",
        headerName: "Type",
        width: 160,
        sortable: false,
        type: "singleSelect",
        valueOptions: VEHICLE_TYPE_OPTIONS,
        renderCell: (params) => {
          if (params.row.isNew) {
            return (
              <EditableSelectCell
                value={params.row.type ?? ""}
                options={VEHICLE_TYPE_OPTIONS}
                hasError={!!params.row.errors?.type}
                onChange={(v) => updateNewRow(params.row.id, { type: v as NewVehicleRow["type"] })}
                placeholder="Type…"
              />
            );
          }
          if (params.row.id === editingRowId && editBuffer) {
            return (
              <EditableSelectCell
                value={editBuffer.type}
                options={VEHICLE_TYPE_OPTIONS}
                hasError={!!editErrors.type}
                onChange={(v) => handleEditBufferChange({ type: v as VehicleTypeValue | "" })}
                placeholder="Type…"
              />
            );
          }
          return VehicleType.FromValue(params.value as VehicleTypeValue)?.label ?? params.value ?? "-";
        },
      },
      {
        field: "category",
        headerName: "Category",
        width: 140,
        sortable: false,
        type: "singleSelect",
        valueOptions: VEHICLE_CATEGORY_OPTIONS,
        renderCell: (params) =>
          params.row.isNew ? (
            <EditableSelectCell
              value={params.row.category ?? "commercial"}
              options={VEHICLE_CATEGORY_OPTIONS}
              hasError={false}
              onChange={(v) => updateNewRow(params.row.id, { category: v as NewVehicleRow["category"] })}
            />
          ) : (
            VehicleCategory.FromValue(params.value as VehicleCategoryValue)?.label ?? params.value ?? "-"
          ),
      },
      {
        field: "actions",
        headerName: "",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isEditingThisRow = params.row.id === editingRowId;

          // Row currently being edited: show Save + Cancel
          if (isEditingThisRow) {
            return (
              <Stack
                direction="row"
                justifyContent="flex-end"
                width="100%"
                divider={<Divider orientation="vertical" flexItem />}
                spacing={2}
              >
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  aria-label="Cancel"
                  sx={{ minWidth: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </Button>
                <Button
                  size="small"
                  color="primary"
                  onClick={handleSave}
                  disabled={isSaving || Object.keys(editErrors).length > 0}
                  aria-label="Save"
                  sx={{ minWidth: 0 }}
                >
                  {isSaving ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                </Button>
              </Stack>
            );
          }

          // New rows: delete only
          if (params.row.isNew) {
            return (
              <Stack
                direction="row"
                justifyContent="flex-end"
                width="100%"
                divider={<Divider orientation="vertical" flexItem />}
                spacing={2}
              >
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => handleDeleteRow(params.row.id)}
                  disabled={disabled}
                  aria-label="Remove row"
                  sx={{ minWidth: 0 }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </Stack>
            );
          }

          // Existing rows not being edited: pencil + delete
          // Pencil is disabled while another row is being edited; delete remains active
          return (
            <Stack
              direction="row"
              justifyContent="flex-end"
              width="100%"
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
            >
              <Button
                size="small"
                color="inherit"
                onClick={() => handleStartEdit(params.row)}
                disabled={disabled || editingRowId !== null}
                aria-label="Edit vehicle"
                sx={{ minWidth: 0, color: "action.active" }}
              >
                <EditIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => handleDeleteRow(params.row.id)}
                disabled={disabled}
                aria-label="Remove vehicle"
                sx={{ minWidth: 0, color: "action.active" }}
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editingRowId,
      editBuffer,
      editErrors,
      isSaving,
      disabled,
      updateNewRow,
      handleEditBufferChange,
      handleStartEdit,
      handleSave,
      handleCancelEdit,
      handleDeleteRow,
    ],
  );
}
